import { m } from '../i18n';
import { WORDS } from './vocab';

/**
 * Worksheet questions (life package): spelling, cloze, money maths, reading and logic.
 * Pure generators so they can be unit-tested; difficulty follows the school grade (3–6).
 */
export type WorksheetKind = 'homework' | 'quiz' | 'exam';
export type Grade = 'A' | 'B' | 'C';

export type Question =
  | { type: 'spell'; word: string; meaning: string; answer: string }
  | { type: 'cloze' | 'math' | 'reading' | 'logic'; prompt: string; passage?: string; options: string[]; answer: string; say?: string };

type Rnd = () => number;
const int = (rnd: Rnd, a: number, b: number) => a + Math.floor(rnd() * (b - a + 1));
const pickOf = <T>(rnd: Rnd, a: readonly T[]): T => a[Math.floor(rnd() * a.length) % a.length];
function shuffled<T>(rnd: Rnd, a: readonly T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
}

/** Four distinct numeric options around the answer (common slips: ±1 step, wrong operation). */
function numOptions(rnd: Rnd, answer: number, slips: number[]): string[] {
  const set = new Set<number>([answer]);
  for (const s of slips) if (s !== answer && s >= 0 && set.size < 4) set.add(s);
  let k = 1;
  while (set.size < 4) {
    const v = answer + (rnd() < 0.5 ? -k : k) * Math.max(1, Math.round(answer * 0.1));
    if (v >= 0) set.add(v);
    k++;
  }
  return shuffled(rnd, [...set].map(String));
}

function meaningOf(w: string): string {
  const t = m();
  return (t.english2.meanings as Record<string, string>)[w] ?? (t.english.meanings as Record<string, string>)[w] ?? w;
}

const MAX_LEN = [5, 5, 5, 5, 7, 9, 14];

export function spellQ(rnd: Rnd, grade: number): Question {
  const max = MAX_LEN[grade] ?? 14;
  const min = grade >= 6 ? 6 : grade >= 5 ? 5 : 3;
  const pool = WORDS.filter((w) => /^[a-z]+$/.test(w.w) && w.w.length <= max && w.w.length >= min);
  const word = pickOf(rnd, pool.length ? pool : WORDS);
  const w = word.w;
  // In English the "meaning" is often the word itself: give the example sentence with a gap instead.
  const mean = meaningOf(w);
  const clue = mean.toLowerCase().includes(w) ? word.ex.replace(new RegExp(`\\b${w}\\b`, 'ig'), '___') : mean;
  return { type: 'spell', word: w, meaning: clue, answer: w };
}

export function clozeQ(rnd: Rnd, grade: number): Question {
  const pool = WORDS.filter((w) => new RegExp(`\\b${w.w}\\b`, 'i').test(w.ex) && /^[a-z]+$/.test(w.w));
  const w = pickOf(rnd, pool);
  // Higher grades get distractors from the same topic (harder to tell apart).
  const same = WORDS.filter((x) => x.w !== w.w && x.cat === w.cat && /^[a-z]+$/.test(x.w));
  const other = WORDS.filter((x) => x.w !== w.w && x.cat !== w.cat && /^[a-z]+$/.test(x.w));
  const from = grade >= 6 && same.length >= 3 ? same : other;
  const opts = shuffled(rnd, from).slice(0, 3).map((x) => x.w);
  return {
    type: 'cloze',
    prompt: w.ex.replace(new RegExp(`\\b${w.w}\\b`, 'i'), '_____'),
    options: shuffled(rnd, [w.w, ...opts]),
    answer: w.w,
    say: w.ex,
  };
}

export function mathQ(rnd: Rnd, grade: number): Question {
  const t = m().life.ws.math;
  const item = pickOf(rnd, t.items);
  let prompt: string, ans: number, slips: number[];
  if (grade <= 3) {
    const a = int(rnd, 4, 9) * 10, b = int(rnd, 8, 25);
    prompt = t.left(a, item, b);
    ans = a - b;
    slips = [a + b, ans + 10, ans - 1];
  } else if (grade === 4) {
    const n = int(rnd, 2, 4), p = int(rnd, 5, 15), payWith = n * p > 50 ? 100 : 50;
    prompt = t.change(n, item, p, payWith);
    ans = payWith - n * p;
    slips = [n * p, payWith - p, ans + p];
  } else if (grade === 5) {
    if (rnd() < 0.5) {
      const p = int(rnd, 2, 8) * 20, d = pickOf(rnd, [10, 25, 50] as const);
      prompt = t.discount(item, p, d);
      ans = p - (p * d) / 100;
      slips = [(p * d) / 100, p - d, ans + 5];
    } else {
      const x = int(rnd, 2, 6) * 5, k = int(rnd, 3, 8);
      prompt = t.weeks(x, item, x * k);
      ans = k;
      slips = [k + 1, k - 1, x * k - x];
    }
  } else {
    if (rnd() < 0.5) {
      const unitA = int(rnd, 6, 12), unitB = unitA + int(rnd, 1, 4), nA = int(rnd, 3, 5), nB = int(rnd, 2, 3);
      prompt = t.unit(item, nA, unitA * nA, nB, unitB * nB);
      ans = unitA;
      slips = [unitB, unitA * nA, unitA + 1];
    } else {
      const p = int(rnd, 1, 5) * 100;
      prompt = t.interest(p);
      ans = Math.round(p * 1.21);
      slips = [p + p / 5, p + p / 10, ans + 10];
    }
  }
  return { type: 'math', prompt, options: numOptions(rnd, ans, slips), answer: String(ans) };
}

export function logicQ(rnd: Rnd, grade: number): Question {
  let seq: number[];
  if (grade <= 3) {
    const a = int(rnd, 1, 9), k = int(rnd, 2, 5);
    seq = [0, 1, 2, 3, 4].map((i) => a + i * k);
  } else if (grade === 4) {
    if (rnd() < 0.5) {
      const a = int(rnd, 1, 4);
      seq = [0, 1, 2, 3, 4].map((i) => a * 2 ** i);
    } else {
      const a = int(rnd, 1, 9);
      seq = [a];
      for (let i = 1; i < 5; i++) seq.push(seq[i - 1] + i);
    }
  } else if (grade === 5) {
    if (rnd() < 0.5) {
      const a = int(rnd, 1, 4);
      seq = [0, 1, 2, 3, 4].map((i) => (a + i) ** 2);
    } else {
      const a = int(rnd, 10, 20), up = int(rnd, 4, 7), down = int(rnd, 1, 3);
      seq = [a];
      for (let i = 1; i < 5; i++) seq.push(seq[i - 1] + (i % 2 ? up : -down));
    }
  } else {
    if (rnd() < 0.5) {
      const a = int(rnd, 1, 3), b = int(rnd, 2, 4);
      seq = [a, b];
      for (let i = 2; i < 5; i++) seq.push(seq[i - 1] + seq[i - 2]);
    } else {
      const a = int(rnd, 1, 3);
      seq = [a];
      for (let i = 1; i < 5; i++) seq.push(seq[i - 1] * 2 + 1);
    }
  }
  const ans = seq[4];
  const d = seq[3] - seq[2];
  return {
    type: 'logic',
    prompt: m().life.ws.logic(seq.slice(0, 4).join(', ')),
    options: numOptions(rnd, ans, [seq[3] + d + 1, seq[3] + d - 1, seq[3] * 2]),
    answer: String(ans),
  };
}

/** Short reading texts (English practice): the first option is the right one. */
export const READINGS: readonly { level: number; text: string; q: string; a: readonly [string, string, string] }[] = [
  { level: 3, text: 'Mia has a red bag. In her bag there is a book, a pencil and an apple.', q: 'What color is Mia’s bag?', a: ['Red', 'Blue', 'Green'] },
  { level: 3, text: 'It is raining today. Ben takes his umbrella and wears his boots to school.', q: 'Why does Ben take an umbrella?', a: ['It is raining', 'It is sunny', 'It is his birthday'] },
  { level: 4, text: 'Amy gets 100 coins every week. She saves 30 coins in her piggy bank and spends the rest on snacks and stickers.', q: 'How many coins does Amy spend?', a: ['70', '30', '100'] },
  { level: 4, text: 'The night market opens at six. Leo and his dad eat stinky tofu, then they play a ring-toss game. Leo wins a small bear.', q: 'What does Leo win?', a: ['A small bear', 'Stinky tofu', 'A ring'] },
  { level: 5, text: 'The TV ad says, “Only today! Buy the new robot now!” Tina wants it, but she decides to wait one day. The next morning she remembers she already has two robots.', q: 'Why is waiting a good idea for Tina?', a: ['She doesn’t really need it', 'The shop is closed', 'The robot is broken'] },
  { level: 5, text: 'Our class planted beans in paper cups. We watered them every morning. After one week, small green leaves came out of the soil.', q: 'What did the class do every morning?', a: ['Watered the beans', 'Ate the beans', 'Painted the cups'] },
  { level: 6, text: 'Typhoons often reach Taiwan in summer. Before a typhoon, families buy water and food, bring plants inside and charge their phones. Schools may close if the winds are strong.', q: 'What is the main idea of the text?', a: ['How people prepare for typhoons', 'Why summer is fun', 'How to charge a phone'] },
  { level: 6, text: 'A combo meal costs 75 coins: a burger, fries and a drink. Bought one by one, they cost 90 coins. But if you only want a burger and water, you pay just 45.', q: 'When is the combo NOT a good deal?', a: ['When you only want the burger', 'When you are very hungry', 'When you want all three'] },
];

export function readingQ(rnd: Rnd, grade: number): Question {
  const near = READINGS.filter((r) => r.level === grade || r.level === grade - 1);
  const r = pickOf(rnd, near.length ? near : READINGS);
  return { type: 'reading', passage: r.text, prompt: r.q, options: shuffled(rnd, r.a), answer: r.a[0], say: r.text };
}

/** Five questions, one of each type; exams are one grade harder and come in random order. */
export function makeWorksheet(kind: WorksheetKind, grade: number, rnd: Rnd = Math.random): Question[] {
  const g = Math.max(3, Math.min(6, Math.round(grade) + (kind === 'exam' ? 1 : 0)));
  const qs = [spellQ(rnd, g), clozeQ(rnd, g), mathQ(rnd, g), readingQ(rnd, g), logicQ(rnd, g)];
  return kind === 'exam' ? shuffled(rnd, qs) : qs;
}

export function gradeOf(score: number, total = 5): Grade {
  const r = score / total;
  return r >= 0.8 ? 'A' : r >= 0.6 ? 'B' : 'C';
}
